import { smallerThan, getWidth, handleError } from 'Helpers'
import Flickity from 'flickity'
import throttle from 'lodash/throttle'
import HTMLElementShim from './ce-shim'

export class BaseCarousel extends HTMLElementShim {
  constructor (options = {}) {
    super()

    const template = document.createElement('template')
    template.innerHTML = `<slot></slot>`
    const shadowRoot = this.attachShadow({ mode: 'closed' })
    shadowRoot.appendChild(template.content.cloneNode(true))

    this._initialIndex = 0
    this._arrowShape = options.arrowShape || null
    this._breakpoint = options.breakpoint || 'sm'
    this._carouselMainSelector = null
    this._cardOuterWidth = null
    this.$carouselMain = null
    this._flickityMain = null

    // Configurable defaults for mobile & desktop
    this._mobileConfig = {
      contain: true,
      wrapAround: false,
      pageDots: true,
      prevNextButtons: false,
      draggable: true,
      lazyLoad: (options.lazyLoad !== undefined) ? options.lazyLoad : 1, // Pre-load 1 extra image on mobile, unless specified in options
      percentPosition: (options.mobilePercentPosition !== undefined) ? options.mobilePercentPosition : true,
      resize: false,
      groupCells: false,
      on: {
        ready: function () {
          // Accessibility - Add aria-label to the page dots container
          let pageDots = this.element.querySelector('.flickity-page-dots')
          if (pageDots) {
            pageDots.setAttribute('aria-label', 'Carousel page navigation')
            pageDots.setAttribute('role', 'list')
          }
        }
      }
    }

    this._desktopConfig = {
      wrapAround: false,
      pageDots: true,
      prevNextButtons: (options.prevNextButtons !== undefined) ? options.prevNextButtons : false,
      draggable: false,
      lazyLoad: (options.lazyLoad !== undefined) ? options.lazyLoad : true, // Only using preloading (a number here) if set in the options
      percentPosition: (options.desktopPercentPosition !== undefined) ? options.desktopPercentPosition : true,
      resize: false,
      groupCells: (options.groupCells !== undefined) ? options.groupCells : false,
      selectedAttraction: 0.1,
      friction: 0.7,
      accessibility: (options.accessibility !== undefined) ? options.accessibility : true,
      arrowShape: this._arrowShape,
      cellAlign: (options.cellAlign !== undefined) ? options.cellAlign : 'center',
      contain: (options.contain !== undefined) ? options.contain : false,
      on: {
        ready: function () {
          // Accessibility - Add aria-label to the page dots container
          let pageDots = this.element.querySelector('.flickity-page-dots')
          if (pageDots) {
            pageDots.setAttribute('aria-label', 'Carousel page navigation')
            pageDots.setAttribute('role', 'list')
          }
        }
      }
    }
  }

  init () {
    this.onResizeHandler = throttle(this.onResize.bind(this), 500)
    window.addEventListener('resize', this.onResizeHandler, false)
  }

  initFlickity () {
    if (!this.$carouselMain) return

    this._activeConfig = smallerThan(this._breakpoint) ? this._mobileConfig : this._desktopConfig
    this._activeConfig.initialIndex = this._initialIndex || 0
    this._flickityMain = new Flickity(this.$carouselMain, this._activeConfig)
    this.$carouselMain.classList.remove('is-preview')
    this.updateFocusableElements()
    this.selectHandler = () => {
      this._initialIndex = this._flickityMain.selectedIndex
      this.updateFocusableElements()
    }
    this._flickityMain.on('select', this.selectHandler)

    this._flickityMain.on('settle', this.preloadNextImage.bind(this))

    // Flickity's lazy load event
    this._flickityMain.on('lazyLoad', (event, cellElement) => {
      const img = cellElement.querySelector('img')
      if (img) {
        // Make lazyload fade in smoother
        img.classList.remove('loading')
        img.classList.add('loaded')
      }
    })
  }

  // Preload the first image of the next group as it is sometimes visible (e.g. testimonial cards)
  // This is to avoid an issue with Flickity's lazy loading and grouped items
  preloadNextImage () {
    // Only run for desktop
    if (!smallerThan(this._breakpoint)) {
      // Check plugin is loaded
      if (!this._flickityMain || typeof this._flickityMain.selectedIndex === 'undefined') return
      // Get group size
      let groupSize = 1
      if (typeof this._flickityMain.options.groupCells === 'number') {
        groupSize = this._flickityMain.options.groupCells
      } else if (this._flickityMain.options.groupCells === true) {
        groupSize = (this._flickityMain.slides[0] && this._flickityMain.slides[0].cells.length) || 1
      }

      // Get next item
      const nextIndex = this._flickityMain.selectedIndex + groupSize

      // If nextIndex is out of bounds, return
      if (nextIndex >= this._flickityMain.cells.length) return

      const nextCell = this._flickityMain.cells[nextIndex]
      if (!nextCell) return

      // Find image
      const img = nextCell.element.querySelector('img[data-flickity-lazyload]')
      if (img) {
        // Set loading class
        img.classList.add('loading')
        img.classList.remove('loaded')

        // Swap to full image
        img.src = img.getAttribute('data-flickity-lazyload')
        img.removeAttribute('data-flickity-lazyload')

        // Fade in when full image is loaded
        img.onload = () => {
          img.classList.remove('loading')
          img.classList.add('loaded')
        }
      }
    }
  }

  destroy () {
    this.destroyFlickity()
    window.removeEventListener('resize', this.onResizeHandler, false)
  }

  getShowableCards () {
    if (!this._cardOuterWidth) {
      return (this.$carouselMain && this.$carouselMain.childElementCount) || 0 // Previously returned true instead of 0
    }
    const availableSpace = parseInt(window.getComputedStyle(this.$carouselMain).width, 10)
    return Math.trunc(availableSpace / this._cardOuterWidth)
  }

  isCarouselNeeded () {
    return this.$carouselMain.childElementCount > this.getShowableCards()
  }

  destroyFlickity () {
    if (this._flickityMain) {
      this._flickityMain.destroy()
    }
  }

  onResize () {
    this.destroyFlickity()
    if (this.isCarouselNeeded()) this.initFlickity()
  }

  connectedCallback () {
    try {
      this._carouselMainSelector = this.getAttribute('data-carousel-main-selector') || null
      this.$carouselMain = this.querySelector(this._carouselMainSelector)
      if (!this.$carouselMain || this.$carouselMain.childElementCount === 0) {
        return
      }

      this._breakpoint = this.getAttribute('data-breakpoint') || this._breakpoint
      this._cardOuterWidth = this.getAttribute('data-card-width') || getWidth(this.$carouselMain.children[0])
      this._arrowShape = this.getAttribute('data-stepper-path') || this._arrowShape

      this.init()

      if (this.isCarouselNeeded()) {
        this.initFlickity()
      } else {
        this.$carouselMain.classList.remove('is-preview')
        // We now use Flickity's lazy loading for carousels
        // We have a separate lazy loading system (vanilla-lazyload) which was previously used instead, but it didn't work well with carousels (DCO-4166).
        // vanilla-lazyload uses the data-src attribute to store the final image URL, Flickity uses data-flickity-lazyload.
        // In image.config.php the full URL is set to the relevant attribute (By the dataSrcTag prop).
        // For carousel items (block-card, block-testimonial-card, product image carousel thumbnails) we use data-flickity-lazyload.
        // When there are not enough cards to warrant a carousel (isCarouselNeeded), we remove the Flickity instance and just display the cards normally.
        // But because this happens in JS at runtime, the PHP config file can't be aware of that, so we need to swap 'data-flickity-lazyload' for 'data-src' here.

        // Change lazyload attribute for images in 'carousels' with only one page to use vanilla-lazyload (as we don't use Flickity from them).
        const lazyImages = this.$carouselMain.querySelectorAll('img[data-flickity-lazyload]')
        lazyImages.forEach(img => {
          img.setAttribute('data-src', img.getAttribute('data-flickity-lazyload'))
          img.removeAttribute('data-flickity-lazyload')
          img.removeAttribute('data-was-processed')

          // Trigger lazy loading for these images as they are not picked up by vanilla-lazyload automatically
          if (window.lazyLoad) {
            window.lazyLoad.load(img)
          }
        })
      }
    } catch (e) {
      handleError(e)
    }
  }

  disconnectedCallback () {
    this.destroy()
  }

  // Accessibility - Add tabindex="-1" to focusable items which are hidden
  updateFocusableElements () {
    // Iterate through items in the carousel
    const slides = this.$carouselMain.querySelectorAll('.flickity-slider > *')

    slides.forEach(slide => {
      const isHidden = slide.getAttribute('aria-hidden') === 'true'

      // Find all focusable elements inside the slide
      const focusables = slide.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      )

      focusables.forEach(el => {
        if (isHidden) {
          // Store original tabindex (if present and not -1) as a data attribute for restoring
          if (
            !el.dataset.originalTabindex &&
            el.hasAttribute('tabindex') &&
            el.getAttribute('tabindex') !== '-1'
          ) {
            el.dataset.originalTabindex = el.getAttribute('tabindex')
          }
          // Set tabindex to unfocusable
          el.setAttribute('tabindex', '-1')
        } else {
          // Restore original tabindex if we stored it
          if (el.dataset.originalTabindex) {
            el.setAttribute('tabindex', el.dataset.originalTabindex)
            delete el.dataset.originalTabindex
          } else {
            // Set tabindex to 0 to ensure focusability
            el.setAttribute('tabindex', '0')
          }
        }
      })
    })
  }
}
